import { useState, useEffect } from "react";
import { useTour, TRAINING_MODULES } from "./TourContext";
import { 
  X, Play, BookOpen, ArrowRight, ArrowLeft, Lightbulb,
  LayoutDashboard, FolderKanban, FileText, Sparkles, Calculator,
  Users, BarChart3, Settings, MessageSquare, Target, Hammer,
  Ruler, ClipboardList, CheckCircle
} from "lucide-react";

// Icon mapping
const ICONS = {
  LayoutDashboard,
  FolderKanban,
  FileText,
  Sparkles,
  Calculator,
  Users,
  BarChart3,
  Settings,
  MessageSquare,
  Target,
  Hammer,
  Ruler,
  ClipboardList,
  CheckCircle
};

// ============== TOUR SPOTLIGHT ==============
export const TourSpotlight = () => {
  const { 
    showTour, 
    tourStep, 
    totalSteps, 
    targetRect, 
    currentStepData,
    nextStep, 
    prevStep, 
    endTour 
  } = useTour();
  
  const [popupStyle, setPopupStyle] = useState({});

  useEffect(() => {
    if (!showTour) return;
    
    if (!targetRect) {
      setPopupStyle({ top: 100, left: window.innerWidth / 2 - 160 });
      return;
    }
    
    const popupWidth = 320;
    const popupHeight = 320;
    const margin = 16;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    let top, left;
    let finalPosition = currentStepData?.position || "bottom";
    
    const spaceAbove = targetRect.top;
    const spaceBelow = viewportHeight - targetRect.bottom;
    const spaceLeft = targetRect.left;
    const spaceRight = viewportWidth - targetRect.right;
    
    if (finalPosition === "bottom" && spaceBelow < popupHeight + margin) {
      finalPosition = spaceAbove > spaceBelow ? "top" : "right";
    }
    if (finalPosition === "top" && spaceAbove < popupHeight + margin) {
      finalPosition = spaceBelow > spaceAbove ? "bottom" : "right";
    }
    if (finalPosition === "right" && spaceRight < popupWidth + margin) {
      finalPosition = "left";
    }
    if (finalPosition === "left" && spaceLeft < popupWidth + margin) {
      finalPosition = "right";
    }
    
    switch (finalPosition) {
      case "right":
        top = targetRect.top + (targetRect.height / 2) - (popupHeight / 2);
        left = targetRect.right + margin;
        break;
      case "left":
        top = targetRect.top + (targetRect.height / 2) - (popupHeight / 2);
        left = targetRect.left - popupWidth - margin;
        break;
      case "bottom":
        top = targetRect.bottom + margin;
        left = targetRect.left + (targetRect.width / 2) - (popupWidth / 2);
        break;
      case "top":
        top = targetRect.top - popupHeight - margin;
        left = targetRect.left + (targetRect.width / 2) - (popupWidth / 2);
        break;
      default:
        top = targetRect.bottom + margin;
        left = targetRect.left;
    }
    
    top = Math.max(margin, Math.min(top, viewportHeight - popupHeight - margin));
    left = Math.max(margin, Math.min(left, viewportWidth - popupWidth - margin));
    
    setPopupStyle({ top, left });
  }, [showTour, targetRect, currentStepData]);

  if (!showTour || !currentStepData) return null;

  const spotlightStyle = targetRect ? {
    position: "fixed",
    top: targetRect.top - 8,
    left: targetRect.left - 8,
    width: targetRect.width + 16,
    height: targetRect.height + 16,
    border: "2px solid #F59E0B",
    borderRadius: "12px",
    boxShadow: "0 0 0 4000px rgba(0,0,0,0.75)",
    transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
    zIndex: 1001,
    pointerEvents: "none"
  } : null;

  return (
    <div className="fixed inset-0 z-[1000]" data-testid="tour-overlay">
      {!targetRect && <div className="fixed inset-0 bg-black/75 z-[1000]" />}
      {spotlightStyle && <div style={spotlightStyle} data-testid="tour-spotlight" />}
      
      <div 
        className="fixed z-[1002] w-[320px] bg-[#1a2233] border border-[rgba(245,158,11,0.3)] rounded-[14px] p-5 shadow-2xl max-h-[90vh] overflow-y-auto"
        style={popupStyle}
        data-testid="tour-popup"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="px-2 py-0.5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-[10px] font-bold">
            Step {tourStep} of {totalSteps}
          </span>
          <button 
            onClick={() => endTour(false)}
            className="text-slate-500 hover:text-slate-300 text-xs"
          >
            Skip tour
          </button>
        </div>
        
        <h3 className="text-white font-bold text-lg mb-2">{currentStepData.title}</h3>
        <p className="text-slate-300 text-sm leading-relaxed mb-3">{currentStepData.description}</p>
        
        {currentStepData.tip && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-[#2A9D9D]/10 border border-[#2A9D9D]/20 mb-4">
            <Lightbulb className="w-4 h-4 text-[#2A9D9D] flex-shrink-0 mt-0.5" />
            <p className="text-[#2A9D9D] text-xs">{currentStepData.tip}</p>
          </div>
        )}
        
        <div className="flex items-center justify-center gap-1.5 mb-4">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div 
              key={i} 
              className={`w-2 h-2 rounded-full transition-colors ${i + 1 === tourStep ? "bg-[#F59E0B]" : "bg-white/20"}`}
            />
          ))}
        </div>
        
        <div className="flex items-center justify-between">
          <button
            onClick={prevStep}
            disabled={tourStep === 1}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              tourStep === 1 
                ? "text-slate-600 cursor-not-allowed" 
                : "text-slate-300 hover:text-white hover:bg-white/5"
            }`}
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            onClick={nextStep}
            className="flex items-center gap-1 px-4 py-1.5 rounded-lg bg-[#F59E0B] text-black text-sm font-bold hover:bg-[#F59E0B]/90 transition-colors"
          >
            {tourStep === totalSteps ? "🎉 Done!" : "Next"} {tourStep !== totalSteps && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== TRAINING PANEL ==============
export const TrainingPanel = () => {
  const { 
    showTrainingPanel, 
    currentTourConfig,
    startTour, 
    closeTrainingPanel, 
    openModulesDrawer 
  } = useTour();

  if (!showTrainingPanel) return null;

  return (
    <div 
      className="fixed bottom-5 right-5 z-[999] w-[280px] bg-[#1a2233] border border-[#F59E0B]/30 rounded-2xl p-5 shadow-2xl animate-in slide-in-from-bottom-5 duration-300"
      data-testid="training-panel"
    >
      <button 
        onClick={closeTrainingPanel}
        className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      
      <div className="text-center mb-4">
        <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center mx-auto mb-3">
          <BookOpen className="w-6 h-6 text-[#F59E0B]" />
        </div>
        <h3 className="text-white font-bold text-lg">Arq+ Training Center</h3>
        <p className="text-slate-400 text-xs mt-1">
          {currentTourConfig ? `Learn: ${currentTourConfig.name}` : "Learn the platform at your own pace"}
        </p>
      </div>
      
      <div className="flex flex-col gap-2">
        <button
          onClick={() => startTour()}
          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl bg-[#F59E0B] text-black font-bold text-sm hover:bg-[#F59E0B]/90 transition-colors"
          data-testid="start-tour-btn"
        >
          <Play className="w-4 h-4" /> Start Tour
        </button>
        <button
          onClick={openModulesDrawer}
          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl border border-[#F59E0B]/50 text-[#F59E0B] font-bold text-sm hover:bg-[#F59E0B]/10 transition-colors"
          data-testid="modules-btn"
        >
          <BookOpen className="w-4 h-4" /> All Modules
        </button>
      </div>
    </div>
  );
};

// ============== MODULES DRAWER ==============
export const ModulesDrawer = () => {
  const { showModulesDrawer, closeModulesDrawer, startTour } = useTour();

  if (!showModulesDrawer) return null;

  return (
    <div 
      className="fixed bottom-[200px] right-5 z-[999] w-[300px] bg-[#1a2233] border border-[#F59E0B]/30 rounded-2xl p-4 shadow-2xl max-h-[450px] overflow-y-auto animate-in slide-in-from-bottom-5 duration-300"
      data-testid="modules-drawer"
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-white font-bold text-sm">Training Modules</h4>
        <button onClick={closeModulesDrawer} className="text-slate-500 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      
      <div className="space-y-2">
        {TRAINING_MODULES.map((module) => {
          const IconComponent = ICONS[module.icon] || BookOpen;
          const isCompleted = localStorage.getItem(`arq_tour_${module.id}_completed`);
          
          return (
            <button
              key={module.id}
              onClick={() => {
                closeModulesDrawer();
                startTour(module.id);
              }}
              className="w-full flex items-start gap-3 p-3 rounded-xl bg-white/[0.03] hover:bg-[#F59E0B]/10 border border-transparent hover:border-[#F59E0B]/30 transition-all text-left group"
              data-testid={`module-${module.id}`}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isCompleted ? "bg-emerald-500/15" : "bg-[#F59E0B]/15"}`}>
                <IconComponent className={`w-4 h-4 ${isCompleted ? "text-emerald-400" : "text-[#F59E0B]"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h5 className="text-white font-semibold text-sm">{module.name}</h5>
                  {isCompleted && (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[8px] font-bold">DONE</span>
                  )}
                </div>
                <p className="text-slate-500 text-[10px] mt-0.5">{module.description}</p>
                <span className="text-[#F59E0B] text-[10px] font-medium">{module.steps} steps</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============== TRAINING BUTTON ==============
export const TrainingButton = ({ className = "" }) => {
  const { openTrainingPanel, currentTourConfig } = useTour();

  return (
    <button
      onClick={openTrainingPanel}
      className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all hover:opacity-90 ${className}`}
      style={{
        background: "rgba(245,158,11,0.12)",
        border: "1px solid rgba(245,158,11,0.25)",
        color: "#F59E0B"
      }}
      data-testid="training-btn"
    >
      <BookOpen className="w-3.5 h-3.5" /> Training
    </button>
  );
};

// ============== COMBINED TOUR UI ==============
export const TourUI = () => {
  return (
    <>
      <TourSpotlight />
      <TrainingPanel />
      <ModulesDrawer />
    </>
  );
};

export default TourUI;
